import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, CircularProgress, Paper, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNotifications } from '../../hooks/useNotifications';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useRequestQueue } from '../../hooks/useRequestQueue';
import { getShoppingLists } from '../../api/shoppingList';
import { ShoppingListSimple } from '../../types/shoppingList';
import { t } from '../../utils/translations';
import { styles } from './ShoppingLists.styles';
import AddListPanel from './AddListPanel';

interface CategorizedLists {
  active: ShoppingListSimple[];
  templates: ShoppingListSimple[];
  archived: ShoppingListSimple[];
}

const ShoppingLists: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotifications();
  const isOnline = useOnlineStatus();
  const [loading, setLoading] = useState<boolean>(true);
  const [addPanelOpen, setAddPanelOpen] = useState<boolean>(false);
  const [lists, setLists] = useState<CategorizedLists>({
    active: [],
    templates: [],
    archived: [],
  });
  const [archivedPage, setArchivedPage] = useState<number>(1);
  const [hasMoreArchived, setHasMoreArchived] = useState<boolean>(false);
  const [archivedPaginationUsed, setArchivedPaginationUsed] = useState<boolean>(false);

  const fetchLists = async (isInitialLoad: boolean = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const [active, templates, archived] = await Promise.all([
        getShoppingLists('1'),
        getShoppingLists('10'),
        getShoppingLists('2', 1),
      ]);

      setHasMoreArchived(archived.length >= 20);

      setLists({
        active,
        templates,
        archived,
      });
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('shoppingLists.loadFailed'),
        t('common.error')
      );
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handleQueueProcessed = async () => {
    // Refresh lists after queue is processed
    await fetchLists(false);
  };

  // Initialize queue hook with callback
  useRequestQueue(handleQueueProcessed);

  const fetchArchivedLists = async (page: number, append: boolean = false) => {
    try {
      const archived = await getShoppingLists('2', page);
      const hasMore = archived.length >= 20;
      setHasMoreArchived(hasMore);

      setLists((prev) => ({
        ...prev,
        archived: append ? [...prev.archived, ...archived] : archived,
      }));

      return { archived, hasMore };
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('shoppingLists.loadFailed'),
        t('common.error')
      );
      return { archived: [], hasMore: false };
    }
  };

  useEffect(() => {
    // Initial fetch - only if online
    if (isOnline) {
      fetchLists(true);
    }

    const intervalId = setInterval(() => {
      if (!archivedPaginationUsed && isOnline) {
        fetchLists(false);
      }
    }, 20000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    const handlePopState = () => {
      if (addPanelOpen) {
        setAddPanelOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [addPanelOpen]);

  const handleOpenAddPanel = () => {
    window.history.pushState({ modalOpen: true }, '');
    setAddPanelOpen(true);
  };

  const handleCloseAddPanel = () => {
    if (addPanelOpen) {
      window.history.back();
    } else {
      setAddPanelOpen(false);
    }
  };

  const handleListCreated = (listId: string) => {
    // First close the panel by setting state directly (without history.back)
    setAddPanelOpen(false);
    // Then navigate to the new list
    navigate(`/shopping-lists/${listId}`);
  };

  const handleShowOlderArchived = async () => {
    setArchivedPaginationUsed(true);
    const nextPage = archivedPage + 1;
    setArchivedPage(nextPage);
    await fetchArchivedLists(nextPage, true);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.section}>
        <Typography variant="h6" component="h2" sx={styles.sectionTitle}>
          {t('shoppingLists.active')} ({lists.active.length})
        </Typography>
        {lists.active.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('shoppingLists.noActiveLists')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {lists.active.map((list) => (
              <Paper
                key={list.id}
                elevation={2}
                onClick={() => navigate(`/shopping-lists/${list.id}`)}
                sx={styles.listPaper(list.status)}
              >
                <Typography variant="h4" component="div" sx={styles.listTitle}>
                  {list.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" align={"right"} sx={styles.listDate}>
                  {new Date(list.createdAt).toLocaleDateString('pl-PL')}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" component="h2" sx={styles.sectionTitle}>
          {t('shoppingLists.templates')} ({lists.templates.length})
        </Typography>
        {lists.templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('shoppingLists.noTemplates')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {lists.templates.map((list) => (
              <Paper
                key={list.id}
                elevation={2}
                onClick={() => navigate(`/shopping-lists/${list.id}`)}
                sx={styles.listPaper(list.status)}
              >
                <Typography variant="h5" component="div" sx={styles.listTitle}>
                  {list.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={styles.listDate} align={"right"}>
                  {new Date(list.createdAt).toLocaleDateString('pl-PL')}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" component="h2" sx={styles.sectionTitle}>
          {t('shoppingLists.archived')} ({lists.archived.length})
        </Typography>
        {lists.archived.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('shoppingLists.noArchivedLists')}
          </Typography>
        ) : (
          <>
            <Stack spacing={1}>
              {lists.archived.map((list) => (
                <Box
                  key={list.id}
                  onClick={() => navigate(`/shopping-lists/${list.id}`)}
                  sx={styles.archivedItem}
                >
                  <Typography variant="body1" component="div">
                    {list.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(list.createdAt).toLocaleDateString('pl-PL')}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {hasMoreArchived && (
              <Button
                variant="text"
                onClick={handleShowOlderArchived}
                fullWidth
                sx={{ marginTop: 2 }}
              >
                {t('shoppingLists.showOlder')}
              </Button>
            )}
          </>
        )}
      </Box>

      <Box sx={styles.addButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddPanel}
          fullWidth
          sx={styles.addButton}
        >
          {t('shoppingLists.addList')}
        </Button>
      </Box>

      <AddListPanel
        open={addPanelOpen}
        onClose={handleCloseAddPanel}
        onListCreated={handleListCreated}
      />
    </Box>
  );
};

export default ShoppingLists;